import {computed, makeObservable, observable} from "mobx";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {Difference} from "./difference";
import {AbstractModel} from "./abstract-model";
import {JournalizedType} from "../stores/journalized-type";

export type OperationType = "add" | "delete" | "update";

export interface JournalProps {
    objectId?: number;
    guid?: string;
    journalizedId?: string;
    journalizedType?: JournalizedType;
    operationType: string;
    notes?: string;
    data?: string;
    creator?: string;
    createdAt?: Date;
    editor?: string;
    updatedAt?: Date;

}

/**
 * Journal
 */
@JsonObject()
export class Journal extends AbstractModel  {

    static readonly comparableAttributes: string[] = [];
    // workaround, because feature template doesn't seem to work for table features
    static readonly prototypeAttributes: string[] = [
        "OBJECTID", "GUID", "JOURNALIZED_ID", "JOURNALIZED_TYPE", "OPERATION_TYPE", "NOTES", "DATA",
        "CREATED_USER", "CREATED_AT", "UPDATED_USER", "UPDATED_AT"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "OBJECTID"],
        ["guid", "GUID"],
        ["journalizedId", "JOURNALIZED_ID"],
        ["journalizedType", "JOURNALIZED_TYPE"],
        ["operationType", "OPERATION_TYPE"],
        ["notes", "NOTES"],
        ["data", "DATA"],
        ["creator", "CREATED_USER"],
        ["createdAt", "CREATED_AT"],
        ["editor", "UPDATED_USER"],
        ["updatedAt", "UPDATED_AT"]
    ]);

    class = "Journal";
    typeLabel = "Journal";

    static sortByDate(a: Journal, b: Journal) {
        const nameA = a.createdAt;
        const nameB = b.createdAt;
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    }

    @JsonProperty({name: "GUID"})
        guid: string;

    /**
     * ID referenziertes Objekt
     */
    @JsonProperty({name: "JOURNALIZED_ID"})
        journalizedId: string;

    /**
     * Typ referenziertes Objekt
     */
    @JsonProperty({name: "JOURNALIZED_TYPE"})
        journalizedType: JournalizedType;

    /**
     * Typ of operation
     *
     * @example: 'delete'
     */
    @JsonProperty({name: "OPERATION_TYPE"})
        operationType: OperationType;

    /**
     * Notiz
     */
    @JsonProperty({name: "NOTES"})
        notes: string;

    /**
     * Daten zugehörig zum Journaleintrag
     * Dienen dazu, dass Änderungen rückgängig gemacht werden können
     * z.B. JSON der Attribute vor einer Änderung
     */
    @JsonProperty({name: "DATA"})
        data: string;

    constructor(props?: JournalProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        // make observable after setting properties to avoid 'strict' messages
        makeObservable(this, {
            objectId: observable, // do observe to reload when object is saved
            guid: observable,
            journalizedId: observable,
            journalizedType: observable,
            operationType: observable,
            notes: observable,
            data: observable,

            isAlterable: computed,
            diffs: computed,

            creator: observable,
            createdAt: observable,
            editor: observable,
            updatedAt: observable,
            isNewRecord: computed
        });
    }

    get comparableAttributes(): string[] {
        return Journal.comparableAttributes;
    }

    get isAlterable(): boolean {
        return true;
    }

    /**
     * Return data as diffs
     */
    get diffs(): Difference[] {
        const json = JSON.parse(this.data);

        if (!json) {
            return [];
        }

        return json.map((d: any) => new Difference(d));
    }

}

